/**
 * Canonical JSON writing, so git diffs show only real changes: a fixed key
 * order, 2-space indents, numbers without floating-point noise, and numeric
 * arrays (such as Tiled tile data) on one line.
 */

export type FileKind = 'project' | 'scene' | 'prefab' | 'json';

const PROJECT_KEYS = ['formatVersion', 'name', 'root', 'resolution', 'scenes', 'plugins', 'settings', 'theme', 'sounds', 'editor'];
const SCENE_KEYS = ['formatVersion', 'key', 'preload', 'animations', 'tilemaps', 'camera', 'lights', 'data', 'children'];
const PREFAB_KEYS = ['formatVersion', 'tilemaps', 'root'];
const OBJECT_KEYS = [
  'id', 'type', 'name', 'prefab', 'map', 'layer',
  'x', 'y', 'scaleX', 'scaleY', 'angle', 'originX', 'originY', 'flipX', 'flipY',
  'depth', 'alpha', 'visible', 'scrollFactorX', 'scrollFactorY', 'blendMode', 'tint',
  'texture', 'animation', 'key', 'font', 'text', 'themeStyle', 'style', 'fontSize', 'align', 'letterSpacing', 'lineSpacing', 'maxWidth',
  'width', 'height', 'leftWidth', 'rightWidth', 'topHeight', 'bottomHeight', 'lit', 'filters',
];
/** Keys that always come last on an object, in this order. Unknown keys sort between. */
const OBJECT_TAIL = ['clips', 'overrides', 'scripts', 'connections', 'data', 'children'];
const TEXTURE_KEYS = ['key', 'frame'];
const ANIMATION_KEYS = ['key', 'autoplay'];
const CLIPS_KEYS = ['file', 'autoplay'];
const TILEMAP_REF_KEYS = ['id', 'file'];
const RESOLUTION_KEYS = ['width', 'height'];

/** Rounds away floating-point noise (0.30000000000000004 becomes 0.3) and normalises -0. */
export function cleanNumber(n: number): number {
  if (!Number.isFinite(n)) throw new Error(`Cannot write non-finite number ${n}`);
  if (Number.isInteger(n)) return n === 0 ? 0 : n;
  const r = Number.parseFloat(n.toFixed(6));
  return r === 0 ? 0 : r;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function ordered(obj: Record<string, unknown>, head: string[], tail: string[] = []): string[] {
  const keys = Object.keys(obj);
  const known = new Set([...head, ...tail]);
  const rest = keys.filter((k) => !known.has(k)).sort();
  return [...head.filter((k) => k in obj), ...rest, ...tail.filter((k) => k in obj)];
}

function sortedAll(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortedAll);
  if (isPlainObject(v)) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v).sort()) out[k] = sortedAll(v[k]);
    return out;
  }
  return v;
}

function reorder(obj: Record<string, unknown>, keys: string[], child: (k: string, v: unknown) => unknown) {
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = child(k, obj[k]);
  return out;
}

function canonObject(v: unknown): unknown {
  if (!isPlainObject(v)) return v;
  return reorder(v, ordered(v, OBJECT_KEYS, OBJECT_TAIL), (k, val) => {
    switch (k) {
      case 'children':
        return Array.isArray(val) ? val.map(canonObject) : val;
      case 'filters':
        return canonFilters(val);
      case 'texture':
        return isPlainObject(val) ? reorder(val, ordered(val, TEXTURE_KEYS), (_, x) => sortedAll(x)) : val;
      case 'animation':
        return isPlainObject(val) ? reorder(val, ordered(val, ANIMATION_KEYS), (_, x) => sortedAll(x)) : val;
      case 'clips':
        return isPlainObject(val) ? reorder(val, ordered(val, CLIPS_KEYS), (_, x) => sortedAll(x)) : val;
      case 'scripts':
        // Scripts run in key order, so their order is kept; each script's values are sorted.
        return isPlainObject(val) ? reorder(val, Object.keys(val), (_, x) => sortedAll(x)) : val;
      case 'overrides':
        // Override values are partial objects; nested ones keep object key order.
        return isPlainObject(val)
          ? reorder(val, Object.keys(val).sort(), (_, x) => canonObject(x))
          : val;
      default:
        return sortedAll(val);
    }
  });
}

/** Filters keep their order (it is the order they run in), each with `type` first. */
function canonFilters(v: unknown): unknown {
  if (!Array.isArray(v)) return v;
  return v.map((f) => (isPlainObject(f) ? reorder(f, ordered(f, ['type', 'enabled']), (_, x) => sortedAll(x)) : f));
}

function canonTilemaps(v: unknown): unknown {
  return Array.isArray(v) ? v.map((t) => (isPlainObject(t) ? reorder(t, ordered(t, TILEMAP_REF_KEYS), (_, x) => x) : t)) : v;
}

/** Returns a copy of `value` with keys in canonical order for its file kind. */
export function canonicalize(value: unknown, kind: FileKind): unknown {
  if (!isPlainObject(value)) return value;
  switch (kind) {
    case 'project':
      return reorder(value, ordered(value, PROJECT_KEYS), (k, v) =>
        k === 'resolution' && isPlainObject(v)
          ? reorder(v, ordered(v, RESOLUTION_KEYS), (_, x) => x)
          : k === 'scenes' || k === 'plugins' ? v : sortedAll(v));
    case 'scene':
      return reorder(value, ordered(value, SCENE_KEYS), (k, v) => {
        if (k === 'children') return Array.isArray(v) ? v.map(canonObject) : v;
        if (k === 'tilemaps') return canonTilemaps(v);
        if (k === 'preload' || k === 'animations') return v;
        if (k === 'camera' && isPlainObject(v)) return reorder(v, Object.keys(v).sort(), (ck, x) => (ck === 'filters' ? canonFilters(x) : sortedAll(x)));
        return sortedAll(v);
      });
    case 'prefab':
      return reorder(value, ordered(value, PREFAB_KEYS), (k, v) => (k === 'root' ? canonObject(v) : k === 'tilemaps' ? canonTilemaps(v) : sortedAll(v)));
    case 'json':
      // Phaser and Tiled files keep their own key order.
      return value;
  }
}

function write(v: unknown, indent: string): string {
  if (v === null) return 'null';
  if (typeof v === 'number') return String(cleanNumber(v));
  if (typeof v === 'string' || typeof v === 'boolean') return JSON.stringify(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '[]';
    if (v.every((x) => typeof x === 'number')) return `[${v.map((x) => String(cleanNumber(x as number))).join(', ')}]`;
    const inner = indent + '  ';
    return `[\n${v.map((x) => inner + write(x === undefined ? null : x, inner)).join(',\n')}\n${indent}]`;
  }
  if (isPlainObject(v)) {
    const keys = Object.keys(v).filter((k) => v[k] !== undefined);
    if (keys.length === 0) return '{}';
    const inner = indent + '  ';
    return `{\n${keys.map((k) => `${inner}${JSON.stringify(k)}: ${write(v[k], inner)}`).join(',\n')}\n${indent}}`;
  }
  throw new Error(`Cannot write value of type ${typeof v}`);
}

/** Writes `value` as canonical JSON text with a trailing newline. */
export function stringifyCanonical(value: unknown, kind: FileKind = 'json'): string {
  return write(canonicalize(value, kind), '') + '\n';
}

/** Guesses a file's kind from its path. */
export function fileKindFor(path: string): FileKind {
  if (path === 'project.json' || path.endsWith('/project.json')) return 'project';
  if (path.endsWith('.scene.json')) return 'scene';
  if (path.endsWith('.prefab.json')) return 'prefab';
  return 'json';
}
